/**
 * CarouselPlayer — public page that renders a published carousel as a
 * web slideshow. Used by /deck (investor) and /welcome-tour
 * (onboarding). Reads the latest published carousel for its mode via
 * GET /api/carousel/latest?mode=...
 *
 * Keyboard: ← / → / Space / Esc. Touch: swipe.
 */
import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Loader2, Download, ExternalLink } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const THEME_BY_MODE = {
  investor:   { bg: "bg-neutral-950", fg: "text-neutral-50", muted: "text-neutral-400", accent: "bg-amber-400 text-neutral-900", stripe: "bg-amber-400", label: "Investor Deck" },
  onboarding: { bg: "bg-white",       fg: "text-slate-900",  muted: "text-slate-500",   accent: "bg-emerald-500 text-white",    stripe: "bg-emerald-500", label: "Welcome Tour" },
};

export default function CarouselPlayer({ mode: forcedMode }) {
  const params = useParams();
  const mode = forcedMode || params.mode || "investor";
  const theme = THEME_BY_MODE[mode] || THEME_BY_MODE.investor;
  const [doc, setDoc] = useState(null);
  const [idx, setIdx] = useState(0);
  const [err, setErr] = useState("");

  useEffect(() => {
    fetch(`${API}/api/carousel/latest?mode=${mode}`)
      .then(r => r.ok ? r.json() : r.json().then(d => Promise.reject(d.detail || `HTTP ${r.status}`)))
      .then(setDoc)
      .catch(e => setErr(String(e)));
  }, [mode]);

  const total = doc?.slides?.length || 0;
  const next = useCallback(() => setIdx(i => Math.min(total - 1, i + 1)), [total]);
  const prev = useCallback(() => setIdx(i => Math.max(0, i - 1)), []);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "ArrowRight" || e.key === " ") next();
      else if (e.key === "ArrowLeft") prev();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  if (err) {
    return (
      <div className={`min-h-screen ${theme.bg} ${theme.fg} flex items-center justify-center p-6`}>
        <div className="max-w-md text-center">
          <div className="font-display text-2xl font-extrabold mb-2">{theme.label} not published yet</div>
          <div className={`text-sm ${theme.muted}`}>
            An admin needs to generate + publish a {mode} carousel from
            /admin/growth-os → Carousel tab.
          </div>
          <div className={`mt-3 text-xs ${theme.muted}`}>Error: {err}</div>
        </div>
      </div>
    );
  }
  if (!doc) {
    return (
      <div className={`min-h-screen ${theme.bg} flex items-center justify-center`}>
        <Loader2 className={`w-8 h-8 animate-spin ${theme.fg}`}/>
      </div>
    );
  }

  const slide = doc.slides[idx];

  return (
    <div className={`min-h-screen ${theme.bg} ${theme.fg} flex flex-col`} data-testid={`carousel-player-${mode}`}>
      {/* Top bar */}
      <header className="flex items-center justify-between px-6 py-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className={`w-1.5 h-8 ${theme.stripe}`}/>
          <div>
            <div className="font-display text-lg font-extrabold">Central Rentals</div>
            <div className={`text-[10px] uppercase tracking-widest ${theme.muted}`}>{theme.label}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className={`text-xs font-bold tabular-nums ${theme.muted}`}>{String(idx + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}</div>
          <a href={`${API}${doc.pdf_url}`} className={`text-xs font-bold inline-flex items-center gap-1 ${theme.muted} hover:opacity-100 opacity-80`} data-testid="player-download-pdf">
            <Download className="w-3 h-3"/> PDF
          </a>
        </div>
      </header>

      {/* Slide */}
      <main className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="max-w-3xl w-full">
          {slide.accent && (
            <div className={`inline-flex items-center px-3 py-1.5 rounded-md text-sm font-extrabold ${theme.accent} mb-6`} data-testid="slide-accent">{slide.accent}</div>
          )}
          <h1 className="font-display text-5xl md:text-7xl font-extrabold leading-[1.05] mb-6" data-testid="slide-headline">{slide.headline}</h1>
          <p className={`text-lg md:text-xl leading-relaxed ${theme.muted}`} data-testid="slide-body">{slide.body}</p>
        </div>
      </main>

      {/* Controls */}
      <footer className="flex items-center justify-between px-6 py-5 border-t border-white/10">
        <button onClick={prev} disabled={idx === 0} className="inline-flex items-center gap-1 px-4 py-2 rounded-lg border border-white/20 text-sm font-bold disabled:opacity-30" data-testid="player-prev">
          <ChevronLeft className="w-4 h-4"/> Prev
        </button>
        <div className="flex items-center gap-1.5" data-testid="player-dots">
          {doc.slides.map((s, i) => (
            <button key={i} onClick={() => setIdx(i)} aria-label={`Slide ${i+1}`}
              className={`w-2 h-2 rounded-full transition-all ${i === idx ? `${theme.stripe} w-6` : "bg-white/30"}`}/>
          ))}
        </div>
        {idx < total - 1 ? (
          <button onClick={next} className={`inline-flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-extrabold ${theme.accent}`} data-testid="player-next">
            Next <ChevronRight className="w-4 h-4"/>
          </button>
        ) : (
          <a href={mode === "investor" ? "mailto:contact@centralrentals.ca?subject=Re%3A%20Central%20Rentals%20deck" : "/signup"} className={`inline-flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-extrabold ${theme.accent}`} data-testid="player-cta">
            {mode === "investor" ? "Reply to discuss" : "Get started"} <ExternalLink className="w-4 h-4"/>
          </a>
        )}
      </footer>
    </div>
  );
}
